/**
 * Unified Subject Mapping System
 *
 * Maps between three subject representations:
 * - slug: Used by Learning Intelligence (e.g., 'civ_pro')
 * - name: Display name used by flashcards (e.g., 'Civil Procedure')
 * - uuid: Database UUID used by junction tables
 *
 * This is the single source of truth for subject references across all features.
 */

export interface SubjectMapping {
  slug: string;           // Learning Intelligence key (e.g., 'civ_pro')
  name: string;           // Display name (e.g., 'Civil Procedure')
  uuid: string;           // Database subjects table UUID
  dbName: string;         // Exact database name (may differ from display name)
  aliases?: string[];     // Alternative names/keys for matching
}

/**
 * Complete subject mapping with all three representations.
 * Keyed by slug (the canonical identifier).
 */
export const SUBJECT_MAP: Record<string, SubjectMapping> = {
  contracts: {
    slug: 'contracts',
    name: 'Contracts',
    uuid: '95b62b70-b878-4212-a0ea-db94aec28118',
    dbName: 'Contracts',
    aliases: ['contract_law'],
  },
  torts: {
    slug: 'torts',
    name: 'Torts',
    uuid: '6a1e7b61-f7ac-490f-b060-c4c69ba1359a',
    dbName: 'Torts',
  },
  civ_pro: {
    slug: 'civ_pro',
    name: 'Civil Procedure',
    uuid: '23f744c8-db79-494a-8ef1-3c10dda97fba',
    dbName: 'Civil Procedure',
    aliases: ['civil_procedure', 'civpro', 'ca_civil_procedure', 'federal rules of civil procedure', 'frcp'],
  },
  property: {
    slug: 'property',
    name: 'Property',
    uuid: '049771e6-588d-428a-ae6f-4db56e43c0ee',
    dbName: 'Real Property',
    aliases: ['real_property'],
  },
  crim_law: {
    slug: 'crim_law',
    name: 'Criminal Law',
    uuid: 'e771d313-da26-4533-a91f-6ad7cdc0ec7f',
    dbName: 'Criminal Law',
    aliases: ['criminal_law', 'criminal', 'crimlaw'],
  },
  crim_pro: {
    slug: 'crim_pro',
    name: 'Criminal Procedure',
    uuid: 'fee144b7-ae21-4b03-b7a1-5165a6f22c19',
    dbName: 'Criminal Procedure',
    aliases: ['criminal_procedure', 'crimpro', 'federal rules of criminal procedure'],
  },
  con_law: {
    slug: 'con_law',
    name: 'Constitutional Law',
    uuid: 'ea1e42a4-cc26-4990-a183-c1628c999c99',
    dbName: 'Constitutional Law',
    aliases: ['constitutional_law', 'constitutional', 'conlaw'],
  },
  evidence: {
    slug: 'evidence',
    name: 'Evidence',
    uuid: '9d9cb547-5608-4c1f-abfe-0f8fc2b3d203',
    dbName: 'Evidence',
    aliases: ['ca_evidence', 'federal rules of evidence', 'fre'],
  },
  wills_trusts: {
    slug: 'wills_trusts',
    name: 'Wills & Trusts',
    uuid: '864dcec8-db16-4d75-ac33-aae3e4a4ca68',
    dbName: 'Wills, Trusts, & Estates',
    aliases: ['wills_and_trusts', 'estates', 'estates_trusts', 'trusts_estates', 'trusts_and_estates'],
  },
  family_law: {
    slug: 'family_law',
    name: 'Family Law',
    uuid: 'd0e95ad1-093d-4c2e-9e84-dbee2904190a',
    dbName: 'Family Law',
    aliases: ['community_property'],
  },
  corporations: {
    slug: 'corporations',
    // NCBE canonical Tier 1 name is "Business Associations" — unified with
    // SubjectCoverage / NCBE_SUBJECTS in Wave C.4. The slug stays
    // `corporations` (legacy signal key) but the display name matches the
    // rest of the exam map.
    name: 'Business Associations',
    uuid: '761ba785-8693-4ede-a3e0-2645dcb2786a',
    dbName: 'Business Associations',
    aliases: ['business_associations', 'corps', 'corporations_law'],
  },
  admin_law: {
    slug: 'admin_law',
    name: 'Administrative Law',
    uuid: '4dd3e025-2db0-41f1-909d-af1de28b0b98',
    dbName: 'Administrative Law',
    aliases: ['administrative_law', 'administrative'],
  },
  secured_trans: {
    slug: 'secured_trans',
    name: 'Secured Transactions',
    uuid: '41ed557a-721b-418e-a6a9-bc573ad89e5e',
    dbName: 'Secured Transactions',
    aliases: ['secured_transactions', 'ucc'],
  },
  conflict_laws: {
    slug: 'conflict_laws',
    name: 'Conflict of Laws',
    uuid: '31375be2-fb26-471c-8e19-134a0509bc88',
    dbName: 'Choice of Law / Conflict of Laws',
    aliases: ['choice_of_law', 'conflicts'],
  },
  legal_writing: {
    slug: 'legal_writing',
    name: 'Legal Writing',
    uuid: 'c964f249-6c9c-4f18-884f-f0c04792a320',
    dbName: 'Legal Writing',
  },
  prof_resp: {
    slug: 'prof_resp',
    name: 'Professional Responsibility',
    uuid: '050a818b-4c38-4529-abc0-04f4eae5e2da',
    dbName: 'Professional Responsibility',
    aliases: ['professional_responsibility', 'ethics', 'mpre'],
  },
  // Additional subjects (originally Issue Spotter-specific, now official)
  remedies: {
    slug: 'remedies',
    name: 'Remedies',
    uuid: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
    dbName: 'Remedies',
  },
  bankruptcy: {
    slug: 'bankruptcy',
    name: 'Bankruptcy',
    uuid: 'b2c3d4e5-f6a7-8901-bcde-f12345678901',
    dbName: 'Bankruptcy',
  },
  ip_law: {
    slug: 'ip_law',
    name: 'Intellectual Property',
    uuid: 'c3d4e5f6-a7b8-9012-cdef-123456789012',
    dbName: 'Intellectual Property',
    aliases: ['copyright_trademarks', 'copyright', 'trademarks', 'ip', 'intellectual_property'],
  },
  fed_income_tax: {
    slug: 'fed_income_tax',
    name: 'Federal Income Tax',
    uuid: '60779cc7-112a-4f9e-9a68-8879690b6cd6',
    dbName: 'Federal Income Tax',
    aliases: ['federal_income_tax', 'income_tax', 'tax', 'taxation'],
  },
  intl_law: {
    slug: 'intl_law',
    name: 'International Law',
    uuid: 'b527e0e6-3ac0-4e12-aa01-91adc1d1e292',
    dbName: 'International Law',
    aliases: ['international_law', 'international', 'pub_intl_law'],
  },
  securities_reg: {
    slug: 'securities_reg',
    name: 'Securities Regulation',
    uuid: '4c36a046-ed21-4bb5-b427-d064a2a1e140',
    dbName: 'Securities Regulation',
    aliases: ['securities_regulation', 'securities', 'sec_reg'],
  },
  unemployment_law: {
    slug: 'unemployment_law',
    name: 'Unemployment Law',
    uuid: 'a7ad4f79-77b7-45ab-8cc4-cadddf09df72',
    dbName: 'Unemployment Law',
    aliases: ['unemployment'],
  },
};

// Build reverse lookup maps for efficient conversion
const uuidToSlugMap = new Map<string, string>();
const nameToSlugMap = new Map<string, string>();
const dbNameToSlugMap = new Map<string, string>();
const aliasToSlugMap = new Map<string, string>();

// Initialize lookup maps
Object.values(SUBJECT_MAP).forEach((mapping) => {
  uuidToSlugMap.set(mapping.uuid, mapping.slug);
  nameToSlugMap.set(mapping.name.toLowerCase(), mapping.slug);
  dbNameToSlugMap.set(mapping.dbName.toLowerCase(), mapping.slug);
  if (mapping.aliases) {
    mapping.aliases.forEach((alias) => {
      aliasToSlugMap.set(alias.toLowerCase(), mapping.slug);
    });
  }
});

/**
 * Convert a slug to its database UUID.
 * @param slug - The subject slug (e.g., 'civ_pro')
 * @returns The UUID or undefined if not found
 */
export function slugToUuid(slug: string): string | undefined {
  return SUBJECT_MAP[slug]?.uuid;
}

/**
 * Convert a database UUID to its slug.
 * @param uuid - The database UUID
 * @returns The slug or undefined if not found
 */
export function uuidToSlug(uuid: string): string | undefined {
  return uuidToSlugMap.get(uuid);
}

/**
 * Convert a display name to its slug.
 * @param name - The display name (e.g., 'Civil Procedure')
 * @returns The slug or undefined if not found
 */
export function nameToSlug(name: string): string | undefined {
  const lowerName = name.toLowerCase();
  return nameToSlugMap.get(lowerName) || dbNameToSlugMap.get(lowerName);
}

/**
 * Convert a slug to its display name.
 * @param slug - The subject slug (e.g., 'civ_pro')
 * @returns The display name or undefined if not found
 */
export function slugToName(slug: string): string | undefined {
  return SUBJECT_MAP[slug]?.name;
}

/**
 * Replace known subject slugs in a text string with their display names.
 * E.g., "rule for con_law" → "rule for Constitutional Law"
 */
export function humanizeSubjectSlugs(text: string): string {
  if (!text) return text;
  return Object.entries(SUBJECT_MAP).reduce((result, [slug, mapping]) => {
    // Match slug as a whole word (bounded by non-alphanumeric or string edges)
    const pattern = new RegExp(`\\b${slug.replace(/_/g, '_')}\\b`, 'g');
    return result.replace(pattern, mapping.name);
  }, text);
}

/**
 * Convert a slug to the exact database name.
 * @param slug - The subject slug (e.g., 'civ_pro')
 * @returns The database name or undefined if not found
 */
export function slugToDbName(slug: string): string | undefined {
  return SUBJECT_MAP[slug]?.dbName;
}

/**
 * Check if a string is a valid UUID format.
 * @param str - String to check
 * @returns True if the string is a valid UUID format
 */
export function isUuid(str: string): boolean {
  if (!str) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
}

/**
 * Normalize any subject input to its canonical slug.
 * Accepts: slug, UUID, display name, database name, or alias.
 *
 * @param input - Any subject identifier
 * @returns The canonical slug or undefined if not recognized
 *
 * @example
 * normalizeSubject('civ_pro') // 'civ_pro'
 * normalizeSubject('Civil Procedure') // 'civ_pro'
 * normalizeSubject('23f744c8-db79-494a-8ef1-3c10dda97fba') // 'civ_pro'
 * normalizeSubject('civil_procedure') // 'civ_pro' (alias)
 */
export function normalizeSubject(input: string): string | undefined {
  if (!input) return undefined;

  // Check if it's a known slug
  if (SUBJECT_MAP[input]) {
    return input;
  }

  // Check if it's a UUID
  if (isUuid(input)) {
    return uuidToSlugMap.get(input);
  }

  const lowerInput = input.toLowerCase();

  // Check display name
  if (nameToSlugMap.has(lowerInput)) {
    return nameToSlugMap.get(lowerInput);
  }

  // Check database name
  if (dbNameToSlugMap.has(lowerInput)) {
    return dbNameToSlugMap.get(lowerInput);
  }

  // Check aliases
  if (aliasToSlugMap.has(lowerInput)) {
    return aliasToSlugMap.get(lowerInput);
  }

  // Return original if not recognized (don't lose data) - matches backend behavior
  return input;
}

/**
 * Get the full mapping for a subject from any identifier.
 * @param input - Any subject identifier (slug, UUID, name, or alias)
 * @returns The full SubjectMapping or undefined if not found
 */
export function getSubjectMapping(input: string): SubjectMapping | undefined {
  const slug = normalizeSubject(input);
  return slug ? SUBJECT_MAP[slug] : undefined;
}

/**
 * Get all subjects as an array (useful for dropdowns).
 * @returns Array of SubjectMapping objects sorted by name
 */
export function getAllSubjects(): SubjectMapping[] {
  return Object.values(SUBJECT_MAP).sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Check if a subject identifier is valid (exists in our mapping).
 * @param input - Any subject identifier
 * @returns True if the subject is recognized
 */
export function isValidSubject(input: string): boolean {
  return normalizeSubject(input) !== undefined;
}
